import React, { useState, useEffect, useMemo } from 'react';
import Button from 'components/button/Button';
import { EitherOrSelection } from 'components/eitherOrSelection/EitherOrSelection';
import { Form, Formik } from 'formik';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import { SelectOption, WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import instance, { ApiResponse } from 'api';
import { endpoints } from 'endpoints.config';
import { Toast } from 'helpers/toast';
import { FormMultiSelectField } from 'components/form/FormMultiSelectField';
import * as Yup from 'yup';
import { ErrorM } from 'components/form/ErrorM';
import { useSelector } from 'react-redux';
import { selectImagePath } from 'reducers/ui';
import {
    NewAccountType,
    AccountTypeAndServicesPayload,
    RegistrationStepProps,
    SignUpFormData,
    RegistrationApiPayload,
    PersonalRegistrationPayload,
} from './models';
import {
    postRegisterCustomerUser,
    postRegisterBusinessAccount,
    postRegisterFinancialAccount,
    ApiReturn,
} from './RegisterApi';
import { handleRegisterFormErrors } from './RegistrationErrorHandling';
import { Spinner } from 'components/spinner/Spinner';
import { useBrandIdentity } from 'hooks/useBrandIdentity';

type CountryItem = {
    name: string;
    isoCode: string;
    id: number;
    personalErrorCode?: string | null;
    businessErrorCode?: string | null;
};

type DropdownOptionsResponse = {
    countries?: CountryItem[];
    productServices?: { name: string; id: number }[];
};

export type AccountTypeAndServicesProps = RegistrationStepProps & {
    accountTypeAndServices: AccountTypeAndServicesPayload | null;
    setAccountTypeAndServices: React.Dispatch<
        React.SetStateAction<AccountTypeAndServicesPayload | null>
    >;
    signUpFormData: SignUpFormData | null;
    onBack?: () => void;
};

const countryRegistrationOptions: SelectOption[] = [
    { label: 'United States', value: 'US' },
    { label: 'Non-US (international)', value: 'NON_US' },
];

const BANKING_PAYMENTS_LABEL = 'banking & payments infrastructure';
const DDA_CHECKING_LABEL = 'dda/checking accounts';

const mockProductOptions: SelectOption[] = [
    {
        label: 'Banking & Payments Infrastructure – Multi-currency accounts and cross-border transfers',
        value: 1,
    },
    { label: 'Crypto OTC – Over-the-counter cryptocurrency trading', value: 2 },
    { label: 'Crypto Acquiring (PORTL) – Cryptocurrency payment acquiring services', value: 3 },
    { label: 'Merchant Services – Accept payments from customers online and in-store', value: 4 },
    { label: 'DDA/Checking Accounts – US-based checking account with debit card', value: 5 },
];

const isUnitedStates = (countryCode: string | null | undefined) => {
    const code = (countryCode ?? '').toString().trim().toUpperCase();
    return code === 'US' || code === 'USA';
};

const optionLabel = (option: SelectOption) => String(option.label).toLowerCase();

/**
 * 75 Pillars only offers Banking & Payments Infrastructure, plus DDA/Checking
 * Accounts when the residential country is the United States.
 * Other brands keep the full list, with DDA hidden for non-US countries.
 */
const filterProductOptions = (
    options: SelectOption[],
    countryCode: string | null | undefined,
    jhGlobalOnly: boolean
): SelectOption[] => {
    const isUS = isUnitedStates(countryCode);
    return options.filter((option) => {
        const label = optionLabel(option);
        const isBanking = label.includes(BANKING_PAYMENTS_LABEL);
        const isDda = label.includes(DDA_CHECKING_LABEL);
        if (jhGlobalOnly) {
            return isBanking || (isUS && isDda);
        }
        return isUS || !isDda;
    });
};

const validationSchema = Yup.object().shape({
    accountType: Yup.string().required('Required').nullable(),
    bConductsThirdPartyPayments: Yup.boolean()
        .nullable()
        .when('accountType', {
            is: (val: string) => val !== NewAccountType.PERSONAL_ACCOUNT,
            then: (schema) => schema.required('Required'),
        }),
    countryOfRegistration: Yup.string().required('Required').nullable(),
    requestedProductIds: Yup.array()
        .of(Yup.number())
        .min(1, 'Required')
        .required('Required')
        .nullable(),
});

export function AccountTypeAndServices({
    translations,
    onComplete,
    accountTypeAndServices,
    setAccountTypeAndServices,
    signUpFormData,
    onBack,
}: AccountTypeAndServicesProps) {
    const imagesPath = useSelector(selectImagePath);
    const { brandKey } = useBrandIdentity();
    // 75 Pillars (jhglobal): personal-only registration. Product choices are
    // Banking & Payments Infrastructure, and DDA/Checking Accounts for US users.
    const isJhGlobal = brandKey === 'jhglobal';
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [productServices, setProductServices] = useState<SelectOption[]>([]);
    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [businessImage, setBusinessImage] = useState<string | null>(null);
    const [personalImage, setPersonalImage] = useState<string | null>(null);
    const [financialImage, setFinancialImage] = useState<string | null>(null);
    const companyName = localStorage?.getItem("companyName")
    const initialValues: AccountTypeAndServicesPayload = accountTypeAndServices || {
        accountType: NewAccountType.PERSONAL_ACCOUNT,
        bConductsThirdPartyPayments: null,
        countryOfRegistration: null,
        requestedProductIds: [],
    };

    useEffect(() => {
        if (!imagesPath) return;
        const brandName = imagesPath.split('/').pop() || 'ibanera';
        const base = `${brandName.charAt(0).toUpperCase() + brandName.slice(1)}`;
        const load = (name: string) =>
            new Promise<string | null>((resolve) => {
                const img = new Image();
                img.onload = () => resolve(`${imagesPath}/${name}`);
                img.onerror = () => resolve(null);
                img.src = `${imagesPath}/${name}`;
            });
        Promise.all([
            load(`${base}Bounce.gif`),
            load(`${base}Bounce.gif`),
            load(`${base}Bounce.gif`),
        ]).then(([b, p, f]) => {
            setBusinessImage(b);
            setPersonalImage(p);
            setFinancialImage(f);
        });
    }, [imagesPath]);

    useEffect(() => {
        instance
            .get<ApiResponse<DropdownOptionsResponse>>(
                endpoints.registration.businessRegistrationDetailsV2
            )
            .then((res) => {
                if (res.data.status === '1') {
                    setCountries(res.data.details.countries ?? []);
                    setProductServices(
                        (res.data.details.productServices ?? []).map((s) => ({
                            label: s.name,
                            value: s.id,
                        }))
                    );
                }
            })
            .catch(() => Toast.openGenericErrorToast());
    }, []);

    const countryOptions = useMemo(
        () => countries.map((c) => ({ label: c.name, value: c.isoCode })),
        [countries]
    );

    const mapAccountTypeToApi = (accountType: NewAccountType | null): string => {
        if (!accountType) return 'Personal';
        switch (accountType) {
            case NewAccountType.PERSONAL_ACCOUNT:
                return 'Personal';
            case NewAccountType.STANDARD_BUSINESS:
                return 'Business';
            case NewAccountType.FINANCIAL_ENTITY:
                return 'Financial';
            default:
                return 'Personal';
        }
    };

    const handleSubmit = async (values: AccountTypeAndServicesPayload) => {
        if (!signUpFormData) {
            setSubmitError(
                'Missing sign-up information. Please go back and complete the sign-up form.'
            );
            return;
        }
        if (!values.countryOfRegistration) {
            setSubmitError('Please select country of registration.');
            return;
        }
        setLoading(true);
        setSubmitError(null);
        const requestedProducts = values.requestedProductIds?.length
            ? values.requestedProductIds
            : [];
        setAccountTypeAndServices({
            ...values,
            requestedProductIds: requestedProducts,
        });
        const isPersonal = values.accountType === NewAccountType.PERSONAL_ACCOUNT;
        const isFinancial = values.accountType === NewAccountType.FINANCIAL_ENTITY;
        let result: ApiReturn;
        if (isPersonal) {
            const personalPayload: PersonalRegistrationPayload = {
                firstName: signUpFormData.firstName.trim(),
                middleNames: signUpFormData.middleNames ? signUpFormData.middleNames.trim() : null,
                lastName: signUpFormData.lastName.trim(),
                currencyCode: 'USD',
                emailAddress: signUpFormData.emailAddress.trim(),
                password: signUpFormData.password,
                confirmPassword: signUpFormData.confirmPassword,
                registeredCountryCode: values.countryOfRegistration,
                phoneNumber: signUpFormData.phoneNumber.trim(),
                bConductsThirdPartyPayments: !!values.bConductsThirdPartyPayments,
                requestedProducts,
            };
            result = await postRegisterCustomerUser(
                endpoints.registration.customerUser,
                personalPayload
            );
        } else if (isFinancial) {
            const financialPayload: RegistrationApiPayload = {
                personalDetails: {
                    firstName: signUpFormData.firstName,
                    middleNames: signUpFormData.middleNames || '',
                    lastName: signUpFormData.lastName,
                    currencyCode: 'USD',
                    emailAddress: signUpFormData.emailAddress,
                    phoneNumber: signUpFormData.phoneNumber,
                    password: signUpFormData.password,
                    confirmPassword: signUpFormData.confirmPassword,
                    nameConfirmed: true,
                },
                accountType: mapAccountTypeToApi(values.accountType),
                bConductsThirdPartyPayments: values.bConductsThirdPartyPayments,
                requestedProducts: values.requestedProductIds?.length
                    ? values.requestedProductIds
                    : null,
                registeredCountryCode: values.countryOfRegistration,
            };
            result = await postRegisterFinancialAccount(financialPayload);
        } else {
            const businessPayload: RegistrationApiPayload = {
                personalDetails: {
                    firstName: signUpFormData.firstName,
                    middleNames: signUpFormData.middleNames || '',
                    lastName: signUpFormData.lastName,
                    currencyCode: 'USD',
                    emailAddress: signUpFormData.emailAddress,
                    phoneNumber: signUpFormData.phoneNumber,
                    password: signUpFormData.password,
                    confirmPassword: signUpFormData.confirmPassword,
                    nameConfirmed: true,
                },
                accountType: mapAccountTypeToApi(values.accountType),
                bConductsThirdPartyPayments: values.bConductsThirdPartyPayments,
                requestedProducts: values.requestedProductIds?.length
                    ? values.requestedProductIds
                    : null,
                registeredCountryCode: values.countryOfRegistration,
            };
            result = await postRegisterBusinessAccount(businessPayload);
        }
        if (result.response) {
            onComplete();
        }
        if (result.errors) {
            const [error] = handleRegisterFormErrors(result.errors[0]);
            setSubmitError(error);
        }
        setLoading(false);
    };

    return (
        <>
            {loading && <Spinner />}
            {translations && (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, touched, setFieldTouched }) => (
                        <Form>
                            <div className="RegisterFormWidth RegisterFormWidthWide">
                                {onBack && (
                                    <Button
                                        priority="tertiary"
                                        className="ChangeNumberButton InlineLink"
                                        onClick={onBack}
                                        type="button"
                                    >
                                        &lt; Back
                                    </Button>
                                )}
                                <h1>Account Type &amp; Services</h1>
                                <div className="FormLabel">
                                    <label>Account Type *</label>
                                </div>
                                <div
                                    className={
                                        isJhGlobal
                                            ? 'SelectAccountType SelectAccountTypeWide SelectAccountTypeCentered'
                                            : 'SelectAccountType SelectAccountTypeWide'
                                    }
                                >
                                    <EitherOrSelection
                                        title="Personal Account"
                                        selected={
                                            values.accountType === NewAccountType.PERSONAL_ACCOUNT
                                        }
                                        onClick={() =>
                                            setFieldValue(
                                                'accountType',
                                                NewAccountType.PERSONAL_ACCOUNT
                                            )
                                        }
                                        image={personalImage ?? null}
                                        value={NewAccountType.PERSONAL_ACCOUNT}
                                        subText="Individual account for personal use"
                                    />
                                    {!isJhGlobal && (
                                        <>
                                            <EitherOrSelection
                                                title="Standard Business"
                                                selected={
                                                    values.accountType ===
                                                    NewAccountType.STANDARD_BUSINESS
                                                }
                                                onClick={() =>
                                                    setFieldValue(
                                                        'accountType',
                                                        NewAccountType.STANDARD_BUSINESS
                                                    )
                                                }
                                                image={businessImage ?? null}
                                                value={NewAccountType.STANDARD_BUSINESS}
                                                subText="Non-financial business entity"
                                            />
                                            <EitherOrSelection
                                                title="Financial Entity"
                                                selected={
                                                    values.accountType ===
                                                    NewAccountType.FINANCIAL_ENTITY
                                                }
                                                onClick={() =>
                                                    setFieldValue(
                                                        'accountType',
                                                        NewAccountType.FINANCIAL_ENTITY
                                                    )
                                                }
                                                image={financialImage ?? null}
                                                value={NewAccountType.FINANCIAL_ENTITY}
                                                subText="Licensed financial institution"
                                            />
                                        </>
                                    )}
                                </div>
                                <ErrorM name="accountType" />
                                <div style={{ marginBottom: 20 }} />
                                {!isJhGlobal &&
                                    values.accountType !== NewAccountType.PERSONAL_ACCOUNT && (
                                    <>
                                        <div className="FormLabel">
                                            <label>
                                                Will you be conducting third party payments? (Money
                                                movement on behalf of others.) *
                                            </label>
                                            <p
                                                className="FormHelperText"
                                                style={{
                                                    marginTop: 4,
                                                    marginBottom: 8,
                                                    fontSize: '0.9em',
                                                    color: 'var(--color-text-secondary, #666)',
                                                }}
                                            >
                                                This means using your {companyName} account to send or
                                                receive money on behalf of others (not just your own
                                                business).
                                            </p>
                                        </div>
                                        <RadioButtons
                                            label=""
                                            options={['Yes', 'No']}
                                            booleanField
                                            fieldname="bConductsThirdPartyPayments"
                                        />
                                        <div style={{ marginBottom: 20 }} />
                                    </>
                                )}
                                <WrappedFormSingleSelectField
                                    fieldName="countryOfRegistration"
                                    options={
                                        countryOptions.length
                                            ? countryOptions
                                            : countryRegistrationOptions
                                    }
                                    onChange={(val) => {
                                        if (val && !touched.countryOfRegistration)
                                            setFieldTouched('countryOfRegistration');
                                        if (isJhGlobal) {
                                            const allowedIds = new Set(
                                                filterProductOptions(
                                                    productServices.length
                                                        ? productServices
                                                        : mockProductOptions,
                                                    val != null ? String(val) : null,
                                                    isJhGlobal
                                                ).map((option) => option.value)
                                            );
                                            const currentIds = values.requestedProductIds ?? [];
                                            const nextIds = currentIds.filter((id) =>
                                                allowedIds.has(id)
                                            );
                                            if (nextIds.length !== currentIds.length) {
                                                setFieldValue('requestedProductIds', nextIds);
                                            }
                                        } else {
                                            setFieldValue('requestedProductIds', []);
                                        }
                                    }}
                                    label="What is the country of the residential address of the individual or the country of registration if this is for a business? *"
                                />
                                <div style={{ marginBottom: 20 }} />
                                <FormMultiSelectField
                                    fieldName="requestedProductIds"
                                    options={filterProductOptions(
                                        productServices.length ? productServices : mockProductOptions,
                                        values.countryOfRegistration,
                                        isJhGlobal
                                    )}
                                    label="What product or service are you looking to open the account for? *"
                                />
                                {submitError && <div className="ErrorMessage">{submitError}</div>}
                                <Button
                                    priority="primary"
                                    variety="full"
                                    className="RegisterFormButton"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {translations.continue_button}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
}
